import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { PageHeader, Section, StatCard, Spinner, RiskBadge } from "@/components/ui-parts";

export function ManagementSummary() {
  const { withScope, activeTenantId } = useTenant();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/dashboard/stats", { params: withScope() }).then(({ data }) => setStats(data));
    // eslint-disable-next-line
  }, [activeTenantId]);
  if (!stats) return <div className="p-8"><Spinner /></div>;
  const weak = [...stats.domains].sort((a, b) => a.score - b.score).slice(0, 5);
  return (
    <div className="p-8 max-w-[1400px]" data-testid="mgmt-page">
      <PageHeader eyebrow="Reports" title="Management Summary"
        description="Operational KPI overview and prioritized focus areas for the security management team." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatCard tone="brand" label="ECRI" value={`${stats.ecri}/100`} />
        <StatCard label="Assessed" value={`${stats.assessed}/${stats.total}`} />
        <StatCard label="Average Maturity" value={stats.avg_score.toFixed(2)} subValue={stats.maturity} />
        <StatCard label="High/Critical Risks" value={stats.high_risk_count} tone={stats.high_risk_count > 20 ? "critical" : "warning"} />
      </div>

      <Section title="Management KPIs">
        <table className="data-table">
          <thead><tr><th>Metric</th><th>Value</th><th>Management Interpretation</th></tr></thead>
          <tbody>
            <tr><td>ECRI</td><td className="mono">{stats.ecri}/100</td><td>Executive-level cyber resilience indicator</td></tr>
            <tr><td>Assessed Requirements</td><td className="mono">{stats.assessed}/{stats.total}</td><td>Coverage completion of the assessment</td></tr>
            <tr><td>Average Maturity</td><td className="mono">{stats.avg_score.toFixed(2)}/5</td><td>Average capability maturity</td></tr>
            <tr><td>High/Critical Risks</td><td className="mono">{stats.high_risk_count}</td><td>Priority risk mitigation areas</td></tr>
          </tbody>
        </table>
      </Section>

      <Section title="Operational Focus Areas">
        <table className="data-table">
          <thead><tr><th>Focus Area</th><th>Reason</th><th>Recommended Action</th></tr></thead>
          <tbody>
            {weak.map((d) => (
              <tr key={d.domain}>
                <td className="text-slate-200">{d.domain}</td>
                <td className="mono">Score {d.score.toFixed(2)}/5</td>
                <td>Assign capability owner, complete evidence and initiate 90-day improvement plan.</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
}

export function CertificationPage() {
  const { withScope, activeTenantId } = useTenant();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/dashboard/stats", { params: withScope() }).then(({ data }) => setStats(data));
    // eslint-disable-next-line
  }, [activeTenantId]);
  if (!stats) return <div className="p-8"><Spinner /></div>;
  const LEVELS = [["Bronze", 50], ["Silver", 65], ["Gold", 80], ["Platinum", 90]];
  const nextLvl = LEVELS.find(([, thr]) => thr > stats.ecri);
  return (
    <div className="p-8 max-w-[1400px]" data-testid="cert-page">
      <PageHeader eyebrow="Reports" title="Certification Readiness"
        description="Track certification tier progress and remaining blocking gaps." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatCard tone="brand" label="Current Level" value={stats.certification} />
        <StatCard label="Next Level" value={nextLvl ? nextLvl[0] : "Maintained"} />
        <StatCard label="Readiness" value={`${stats.ecri}%`} />
        <StatCard label="Blocking Gaps" value={stats.top_gaps.filter((r) => r.score < 3).length} tone="warning" />
      </div>

      <Section title="Certification Ladder">
        <table className="data-table">
          <thead><tr><th>Level</th><th>Threshold</th><th>Status</th><th>Gap to Level</th></tr></thead>
          <tbody>
            {LEVELS.map(([lvl, thr]) => (
              <tr key={lvl}>
                <td className="text-slate-200">{lvl}</td>
                <td className="mono">{thr}/100</td>
                <td><span className={`badge ${stats.ecri >= thr ? "badge-success" : "badge-low"}`}>{stats.ecri >= thr ? "Ready" : "Not Ready"}</span></td>
                <td className="mono">{stats.ecri >= thr ? "0" : thr - stats.ecri}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
}

export function RiskRegisterPage() {
  const { withScope, activeTenantId } = useTenant();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/dashboard/risk-register", { params: withScope() }).then(({ data }) => setRows(data));
    // eslint-disable-next-line
  }, [activeTenantId]);
  if (!rows) return <div className="p-8"><Spinner /></div>;
  return (
    <div className="p-8 max-w-[1800px]" data-testid="risk-page">
      <PageHeader eyebrow="Reports" title="Risk Register"
        description="Consolidated register of high and critical residual risk requirements with recommended treatment." />
      <Section>
        <div className="overflow-x-auto" style={{ maxHeight: "75vh" }}>
          <table className="data-table">
            <thead><tr>
              <th>Risk ID</th><th>Requirement</th><th>Domain</th><th>Capability</th>
              <th>Risk Level</th><th>Score</th><th>Risk Statement</th><th>Treatment</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.riskId}>
                  <td className="mono">{r.riskId}</td>
                  <td className="mono">{r.requirementId}</td>
                  <td>{r.domain}</td>
                  <td>{r.capability}</td>
                  <td><RiskBadge level={r.riskLevel} /></td>
                  <td className="mono">{r.score.toFixed(1)}</td>
                  <td className="text-slate-300 max-w-md">{r.statement}</td>
                  <td className="text-slate-400 max-w-xs">{r.treatment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

export function ImprovementPage() {
  const { withScope, activeTenantId } = useTenant();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/dashboard/improvement-plan", { params: withScope() }).then(({ data }) => setRows(data));
    // eslint-disable-next-line
  }, [activeTenantId]);
  if (!rows) return <div className="p-8"><Spinner /></div>;
  return (
    <div className="p-8 max-w-[1800px]" data-testid="improvement-page">
      <PageHeader eyebrow="Reports" title="Improvement Plan"
        description="Prioritized capability improvement plan with target scores, owners and suggested timelines." />
      <Section>
        <div className="overflow-x-auto" style={{ maxHeight: "75vh" }}>
          <table className="data-table">
            <thead><tr>
              <th>Priority</th><th>Requirement</th><th>Capability</th>
              <th>Current</th><th>Target</th><th>Improvement Action</th><th>Owner</th><th>Timeline</th>
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.requirementId + i}>
                  <td><span className={`badge ${r.priority === "P1" ? "badge-critical" : r.priority === "P2" ? "badge-high" : "badge-medium"}`}>{r.priority}</span></td>
                  <td className="mono">{r.requirementId}</td>
                  <td>{r.capability}</td>
                  <td className="mono">{r.currentScore.toFixed(1)}</td>
                  <td className="mono">{r.targetScore.toFixed(1)}</td>
                  <td className="text-slate-300 max-w-md">{r.action}</td>
                  <td className="text-slate-400">{r.owner}</td>
                  <td className="mono text-slate-400">{r.timeline}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

export function RoadmapPage() {
  const { withScope, activeTenantId } = useTenant();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/dashboard/roadmap", { params: withScope() }).then(({ data }) => setData(data));
    // eslint-disable-next-line
  }, [activeTenantId]);
  if (!data) return <div className="p-8"><Spinner /></div>;
  const cols = [
    { title: "0–30 Days", key: "days_0_30", accent: "border-red-500/40" },
    { title: "31–60 Days", key: "days_31_60", accent: "border-amber-500/40" },
    { title: "61–90 Days", key: "days_61_90", accent: "border-emerald-500/40" },
  ];
  return (
    <div className="p-8 max-w-[1600px]" data-testid="roadmap-page">
      <PageHeader eyebrow="Reports" title="90-Day Roadmap"
        description="Phased execution plan for the top improvement priorities." />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {cols.map((c) => (
          <div key={c.key} className={`card border-t-2 ${c.accent}`}>
            <div className="px-5 py-3 border-b border-slate-800/70">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Phase</div>
              <div className="font-heading text-lg font-bold text-slate-100">{c.title}</div>
            </div>
            <ul className="p-5 space-y-2">
              {(data[c.key] || []).map((r) => (
                <li key={r.requirementId} className="text-xs border-l-2 border-slate-800 pl-3 py-1">
                  <div className="text-slate-200">{r.capability}</div>
                  <div className="mono text-slate-500 text-[10px]">{r.requirementId}</div>
                </li>
              ))}
              {(!data[c.key] || data[c.key].length === 0) && <li className="text-xs text-slate-500">No items in this phase.</li>}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ScoringGuidePage() {
  const rows = [
    ["0", "Not Implemented", "No evidence or implementation."],
    ["1", "Initial", "Ad hoc, inconsistent, limited evidence."],
    ["2", "Managed", "Documented and repeatable."],
    ["3", "Integrated", "Integrated with governance, operations and reporting."],
    ["4", "Adaptive", "Measured, optimized, continuously validated."],
    ["5", "Autonomous", "AI-assisted, self-improving, governed automation."],
  ];
  return (
    <div className="p-8 max-w-[1200px]" data-testid="scoring-page">
      <PageHeader eyebrow="Assessment" title="Scoring Guide"
        description="GACRF Enterprise Capability Maturity Model (ECMM) 0-5 scale definitions." />
      <Section>
        <table className="data-table">
          <thead><tr><th>Score</th><th>Meaning</th><th>Interpretation</th></tr></thead>
          <tbody>
            {rows.map(([s, m, d]) => (
              <tr key={s}><td className="mono text-lg">{s}</td><td className="text-slate-100 font-semibold">{m}</td><td>{d}</td></tr>
            ))}
          </tbody>
        </table>
      </Section>
    </div>
  );
}
